import React from 'react';
import { useSiteSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import Login from '../pages/Login';
import Home from '../pages/Home';
import { ShieldAlert, LogOut, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { LOGO_URL, COMPANY } from '../mock';

// Determines if the current browser hostname matches a "staff-only" domain entry.
// Supports exact matches and a leading "*." wildcard for subdomain coverage.
// Also AUTO-DETECTS Emergent deployment/preview URLs and treats them as staff-only
// so the deployed app is always the employee portal, while custom domains
// (e.g. the Squarespace customer site) show the public storefront.
export function isStaffOnlyHost(staffDomains, host) {
  const h = (host || '').toLowerCase();

  // Auto: any Emergent-hosted URL is the employee portal
  if (h.endsWith('.emergentagent.com') || h.endsWith('.emergent.host') || h === 'localhost' || h === '127.0.0.1') {
    return true;
  }

  for (const raw of staffDomains || []) {
    const d = (raw || '').toLowerCase().trim();
    if (!d) continue;
    if (d.startsWith('*.')) {
      const base = d.slice(2);
      if (h === base || h.endsWith('.' + base)) return true;
    } else if (h === d || h === 'www.' + d || 'www.' + h === d) {
      return true;
    }
  }
  return false;
}

// Landing root:
//  - If hostname is in staff_only_domains → show Staff Sign-in (or NotAuthorized if signed in non-admin)
//  - Else if public site published OR user is admin → show Home
//  - Else → Staff Sign-in
export default function LandingGate() {
  const { settings, loading } = useSiteSettings();
  const { user, loading: authLoading, logout } = useAuth();

  if (loading || authLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#e11d2c] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const staffOnly = isStaffOnlyHost(settings?.staff_only_domains, host);
  const published = !!settings?.public_site_published;

  // Customer-facing customers: render Home
  if (!staffOnly && (published || user?.role === 'admin')) return <Home />;

  // Staff-only URL: even if site is published, if visitor isn't admin → show sign-in/not authorized
  if (user?.role === 'admin') return <Home />;

  // Signed-in customer account (non-admin) → published storefront, never the admin gate
  if (user && published) return <Home />;

  // Signed in but not authorized as admin → clear message (no loop)
  if (user) {
    return (
      <section className="min-h-[80vh] bg-black text-white relative overflow-hidden flex items-center">
        <div className="absolute -top-32 -right-32 w-[440px] h-[440px] rounded-full bg-[#e11d2c]/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[440px] h-[440px] rounded-full bg-[#e11d2c]/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-md mx-auto px-5 lg:px-8 w-full">
          <div className="bg-white/[0.06] backdrop-blur border border-white/10 rounded-2xl p-8 lg:p-10">
            <div className="flex justify-center">
              <img src={LOGO_URL} alt="Timeless" className="w-24 h-24 object-contain rounded-2xl" />
            </div>
            <div className="mt-6 w-12 h-12 mx-auto rounded-full bg-[#e11d2c]/20 grid place-items-center text-[#e11d2c]">
              <ShieldAlert size={22} />
            </div>
            <h1 className="font-display text-[24px] sm:text-[28px] font-black text-center mt-4">You're signed in — but not authorized as admin</h1>
            <p className="text-white/65 text-[14px] text-center mt-3 leading-relaxed">
              Your Google account <b className="text-white break-all">{user.email}</b> isn't on the admin allow-list yet.
            </p>
            <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4 mt-5 text-[13px] text-white/75 leading-relaxed">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#e11d2c] mb-2">How to fix</div>
              Ask an existing admin to add this email at <b className="text-white">/admin/settings → Admin Users</b>, or sign in with the authorized owner email (<a className="text-white underline" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>).
            </div>
            <Button onClick={logout} className="w-full mt-6 h-11 rounded-full bg-[#e11d2c] hover:bg-[#c01526] text-white font-semibold text-[14px]">
              <LogOut size={15} className="mr-2" /> Sign out & try another account
            </Button>
            <a href={`mailto:${COMPANY.email}?subject=Please%20add%20me%20as%20admin&body=Hi%2C%0A%0APlease%20add%20${encodeURIComponent(user.email)}%20as%20an%20admin%20user%20for%20Timeless%20Auto%20Imports.%0A%0AThanks.`}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 h-11 rounded-full border border-white/15 text-white/85 hover:bg-white/5 font-semibold text-[13px]">
              <Mail size={14} /> Email admin to request access
            </a>
          </div>
        </div>
      </section>
    );
  }

  return <Login />;
}
